import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ArrowDown, ArrowUp, ChevronsUpDown, Filter, X } from 'lucide-react';
import { useState } from 'react';
import { cn } from "../lib/cn.js";
import { Button } from "./button.js";
import { DateField } from "./date-field.js";
import { Input, Select } from "./input.js";
import { MoneyInput } from "./money-input.js";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger, } from "./overlay.js";
import { SORT_LABELS } from "./table.js";
export const emptyFilterValue = (spec) => {
    switch (spec.kind) {
        case 'text':
            return { kind: 'text', text: '' };
        case 'enum':
            return { kind: 'enum', value: '' };
        case 'range':
            return { kind: 'range', min: '', max: '' };
        case 'dateRange':
            return { kind: 'dateRange', from: '', to: '' };
    }
};
/** Se este filtro está mesmo restringindo alguma coisa. */
export const isFilterActive = (value) => {
    if (value === undefined)
        return false;
    switch (value.kind) {
        case 'text':
            return value.text.trim() !== '';
        case 'enum':
            return value.value !== '';
        case 'range':
            return value.min !== '' || value.max !== '';
        case 'dateRange':
            return value.from !== '' || value.to !== '';
    }
};
const FilterFields = ({ spec, draft, onDraft, }) => {
    if (spec.kind === 'text' && draft.kind === 'text') {
        return (_jsx(Input, { autoFocus: true, value: draft.text, placeholder: spec.placeholder ?? 'Contém…', "aria-label": "Filtrar", onChange: (event) => {
                onDraft({ kind: 'text', text: event.target.value });
            } }));
    }
    if (spec.kind === 'enum' && draft.kind === 'enum') {
        return (_jsxs(Select, { autoFocus: true, value: draft.value, "aria-label": "Filtrar", onChange: (event) => {
                onDraft({ kind: 'enum', value: event.target.value });
            }, children: [_jsx("option", { value: "", children: "Todos" }), spec.options.map((option) => (_jsx("option", { value: option.value, children: option.label }, option.value)))] }));
    }
    if (spec.kind === 'range' && draft.kind === 'range') {
        const money = spec.unit === 'money';
        return (_jsxs("div", { className: "flex items-center gap-2", children: [_jsx(MoneyInput, { autoFocus: true, value: draft.min, symbol: money, scale: money ? 2 : 6, "aria-label": "De", placeholder: "De", onValueChange: (next) => {
                        onDraft({ ...draft, min: next });
                    } }), _jsx("span", { className: "text-(--text-muted)", children: "at\u00E9" }), _jsx(MoneyInput, { value: draft.max, symbol: money, scale: money ? 2 : 6, "aria-label": "At\u00E9", placeholder: "At\u00E9", onValueChange: (next) => {
                        onDraft({ ...draft, max: next });
                    } })] }));
    }
    if (spec.kind === 'dateRange' && draft.kind === 'dateRange') {
        return (_jsxs("div", { className: "flex items-center gap-2", children: [_jsx(DateField, { autoFocus: true, value: draft.from, "aria-label": "De", onChange: (event) => {
                        onDraft({ ...draft, from: event.target.value });
                    } }), _jsx("span", { className: "text-(--text-muted)", children: "at\u00E9" }), _jsx(DateField, { value: draft.to, "aria-label": "At\u00E9", onChange: (event) => {
                        onDraft({ ...draft, to: event.target.value });
                    } })] }));
    }
    return null;
};
export const FilterableHeaderCell = ({ className, numeric = false, sorted = false, onSort, kind, filter, value, onFilter, children, ...props }) => {
    const columnKind = kind ?? (numeric ? 'number' : 'text');
    const active = isFilterActive(value);
    /*
     * O primeiro clique numa coluna ainda não ordenada — a mesma regra do
     * `SortableHeaderCell`, e escrita uma vez só lá não dá: são dois componentes.
     * Nome lê A→Z; dinheiro e data leem maior e mais recente primeiro.
     */
    const first = columnKind === 'text' ? 'asc' : 'desc';
    const nextDirection = sorted === false ? first : sorted === 'asc' ? 'desc' : 'asc';
    const SortIcon = sorted === 'asc' ? ArrowUp : sorted === 'desc' ? ArrowDown : ChevronsUpDown;
    /*
     * O nome da coluna, para o leitor de tela saber de QUAL filtro se trata.
     * Todas as telas passam texto; se um dia passarem um nó, o rótulo continua
     * dizendo o que o botão faz, só que sem o nome.
     */
    const columnName = typeof children === 'string' ? children : '';
    /*
     * O rascunho existe para o filtro só valer quando a pessoa mandar.
     *
     * Disparar a consulta a cada tecla numa faixa de preço pediria ao servidor
     * "de R$ 1", "de R$ 12", "de R$ 123" — três respostas jogadas fora e a lista
     * piscando embaixo do menu aberto.
     */
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState(null);
    const current = draft ?? value ?? (filter === undefined ? null : emptyFilterValue(filter));
    const apply = () => {
        if (onFilter === undefined || current === null)
            return;
        onFilter(isFilterActive(current) ? current : null);
        setDraft(null);
        setOpen(false);
    };
    const clear = () => {
        if (onFilter === undefined)
            return;
        onFilter(null);
        setDraft(null);
        setOpen(false);
    };
    return (_jsx("th", { scope: "col", "aria-sort": sorted === false
            ? 'none'
            : sorted === 'asc'
                ? 'ascending'
                : 'descending', className: cn('p-0 text-(length:--text-xs) font-(--weight-semibold) uppercase tracking-wide', 'text-(--text-secondary) whitespace-nowrap', className), ...props, children: _jsxs("div", { className: cn('flex w-full items-center', numeric ? 'justify-end' : 'justify-between'), children: [onSort === undefined ? null : (_jsxs("button", { type: "button", onClick: () => {
                        onSort(nextDirection);
                    }, title: `Ordenar ${SORT_LABELS[columnKind][nextDirection]}`, className: cn('flex min-w-0 flex-1 items-center gap-1 px-3 py-1.5', 'uppercase tracking-wide transition-colors duration-(--duration-fast)', 'hover:text-(--text-primary)', sorted === false ? '' : 'text-(--text-primary)', numeric ? 'justify-end' : 'justify-start'), children: [numeric ? (_jsx(SortIcon, { "aria-hidden": "true", className: cn('h-3 w-3 shrink-0', sorted === false ? 'opacity-40' : '') })) : null, _jsx("span", { className: "truncate", children: children }), numeric ? null : (_jsx(SortIcon, { "aria-hidden": "true", className: cn('h-3 w-3 shrink-0', sorted === false ? 'opacity-40' : '') }))] })), onSort === undefined && filter === undefined ? (_jsx("span", { className: "truncate px-3 py-1.5", children: children })) : null, filter === undefined ? null : (_jsxs(DropdownMenu, { open: open, onOpenChange: (next) => {
                        setOpen(next);
                        // Fechar sem aplicar descarta o rascunho: o que vale é o que a
                        // lista está mostrando, não o que ficou digitado.
                        if (!next)
                            setDraft(null);
                    }, children: [_jsx(DropdownMenuTrigger, { asChild: true, children: onSort === undefined ? (_jsxs("button", { type: "button", className: cn('flex w-full items-center gap-1 px-3 py-1.5', 'uppercase tracking-wide transition-colors duration-(--duration-fast)', 'hover:text-(--text-primary)', active ? 'text-(--text-primary)' : '', numeric ? 'justify-end' : 'justify-start'), children: [_jsx("span", { className: "truncate", children: children }), _jsx(Filter, { "aria-hidden": "true", className: cn('h-3 w-3 shrink-0', active ? 'text-(--brand-primary)' : 'opacity-40') })] })) : (_jsx("button", { type: "button", "aria-label": columnName === ''
                                    ? 'Filtrar esta coluna'
                                    : `Filtrar por ${columnName}`, "aria-pressed": active, className: cn('flex h-6 w-6 shrink-0 items-center justify-center', 'mr-1 rounded-(--radius-sm)', 'transition-colors duration-(--duration-fast)', 'hover:bg-(--surface-sunken) hover:text-(--text-primary)'), children: _jsx(Filter, { "aria-hidden": "true", className: cn('h-3 w-3', active ? 'text-(--brand-primary)' : 'opacity-40') }) })) }), _jsx(DropdownMenuContent, { align: numeric ? 'end' : 'start', children: current === null ? null : (_jsxs("div", { className: "flex flex-col gap-2 p-2", 
                                /*
                                 * O menu do Radix escuta as teclas para andar entre os itens.
                                 * Sem isto, digitar "cabo" no filtro move o foco para os
                                 * itens que começam com C, A, B e O — e o campo perde as
                                 * letras.
                                 */
                                onKeyDown: (event) => {
                                    event.stopPropagation();
                                    if (event.key === 'Enter')
                                        apply();
                                }, children: [_jsx(FilterFields, { spec: filter, draft: current, onDraft: setDraft }), _jsxs("div", { className: "flex justify-end gap-2", children: [_jsx(Button, { variant: "ghost", size: "sm", onClick: clear, children: "Limpar" }), _jsx(Button, { variant: "primary", size: "sm", onClick: apply, children: "Aplicar" })] })] })) })] }))] }) }));
};
export const ActiveFilters = ({ chips, onClearAll, className, }) => {
    if (chips.length === 0)
        return null;
    return (_jsxs("div", { "aria-label": "Filtros ativos", className: cn('flex flex-wrap items-center gap-2', className), children: [chips.map((chip) => (_jsxs("button", { type: "button", onClick: chip.onRemove, className: cn('inline-flex items-center gap-1 rounded-(--radius-full)', 'border border-(--border-subtle) bg-(--surface-sunken)', 'px-2 py-0.5 text-(length:--text-xs) text-(--text-secondary)', 'hover:text-(--text-primary)'), children: [chip.label, _jsx(X, { "aria-label": `Tirar filtro ${chip.label}`, className: "h-3 w-3" })] }, chip.id))), chips.length < 2 ? null : (_jsx(Button, { variant: "ghost", size: "sm", onClick: onClearAll, children: "Limpar tudo" }))] }));
};
//# sourceMappingURL=column-filter.js.map